// ============================================================
// CHECKOUT PAGE — /checkout
// ============================================================
// Protetta da PrivateRoute in App.tsx — l'utente arriva qui
// già autenticato (il login avviene nel CartDrawer).
//
// Un solo pulsante di pagamento: tutti i metodi (carta, PayPal,
// Satispay, Google Pay, Amazon Pay, Klarna, Scalapay) sono
// abilitati direttamente in Stripe Dashboard e vengono mostrati
// automaticamente dentro l'Embedded Checkout — non serve nessuna
// integrazione separata né una scelta preventiva qui.

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from '@stripe/react-stripe-js';
import { useCart } from '../../../contexts/CartContext';
import { useAuth } from '../../../contexts/AuthContext';
import { orderService } from '../../../services';
import {
  STRIPE_PUBLISHABLE_KEY,
  USE_MOCKS,
} from '../../../config/serviceConfig';
import { MOCK_CHECKOUT_SESSION_ID } from '../../../services/mock/order.mock';
import { ApiError } from '../../../services/api.client';
import BillingDetailsForm, {
  type BillingDetailsFormHandle,
} from './BillingDetailsForm';
import './Checkout.css';

const PAYMENT_METHODS = [
  'Carta',
  'PayPal',
  'Satispay',
  'Google Pay',
  'Klarna',
  'Scalapay',
];

// Caricato una sola volta a livello di modulo, non ad ogni render.
const stripePromise = STRIPE_PUBLISHABLE_KEY
  ? loadStripe(STRIPE_PUBLISHABLE_KEY)
  : null;

const Checkout: React.FC = () => {
  const { items, totalPrice, openCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [payError, setPayError] = useState('');
  const billingFormRef = useRef<BillingDetailsFormHandle>(null);

  // Se il carrello è vuoto torna alla pagina corsi
  useEffect(() => {
    if (items.length === 0 && !clientSecret) {
      navigate('/rimettiti-al-centro', { replace: true });
    }
  }, [items, clientSecret, navigate]);

  // ── Avvia il pagamento ────────────────────────────────────
  const handlePayment = async () => {
    setPayError('');

    const billing = billingFormRef.current?.validate();
    if (!billing) return;

    if (!USE_MOCKS && !stripePromise) {
      setPayError('Pagamento non configurato. Contatta il supporto.');
      return;
    }

    setIsCreatingSession(true);
    try {
      const { clientSecret: secret } = await orderService.createCheckoutSession(
        items.map((i) => i.course.id),
        billing
      );

      // In modalità mock non esiste un vero iframe Stripe da montare:
      // la sessione è considerata già pagata, si va diretti alla conferma.
      if (USE_MOCKS) {
        navigate(`/ordine-ok?session_id=${MOCK_CHECKOUT_SESSION_ID}`, {
          replace: true,
        });
        return;
      }

      setClientSecret(secret);
    } catch (err) {
      setPayError(
        err instanceof ApiError || err instanceof Error
          ? err.message
          : 'Errore durante la creazione del pagamento. Riprova più tardi.'
      );
    } finally {
      setIsCreatingSession(false);
    }
  };

  // ── Torna agli acquisti ───────────────────────────────────
  const handleBack = () => {
    navigate('/rimettiti-al-centro');
    setTimeout(() => openCart(), 300); // apre il drawer dopo la navigazione
  };

  if (items.length === 0 && !clientSecret) return null;

  // ── Modulo di pagamento Stripe incorporato ────────────────
  if (clientSecret && stripePromise) {
    return (
      <div className="checkout-page">
        <div className="checkout-topbar">
          <button className="checkout-back-btn" onClick={handleBack}>
            <i className="bi bi-arrow-left me-2" />
            Torna agli acquisti
          </button>
        </div>
        <div className="container py-5">
          <EmbeddedCheckoutProvider
            stripe={stripePromise}
            options={{ clientSecret }}
          >
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      {/* Top bar con back button */}
      <div className="checkout-topbar">
        <button className="checkout-back-btn" onClick={handleBack}>
          <i className="bi bi-arrow-left me-2" />
          Torna agli acquisti
        </button>
      </div>

      <div className="container py-5">
        <div className="row justify-content-center g-4">
          {/* ── Colonna sinistra: pagamento ── */}
          <div className="col-12 col-lg-7">
            <div className="checkout-card">
              <h1 className="checkout-section-title">
                <i className="bi bi-credit-card me-2" />
                Pagamento
              </h1>

              {/* Dati utente */}
              {user && (
                <div className="checkout-user-info">
                  <i className="bi bi-person-check-fill me-2 text-success" />
                  <span>
                    Acquisto come{' '}
                    <strong>
                      {user.firstName} {user.lastName}
                    </strong>
                    <span className="text-muted ms-1">({user.email})</span>
                  </span>
                </div>
              )}

              {/* Dati di fatturazione */}
              <BillingDetailsForm
                ref={billingFormRef}
                initialFirstName={user?.firstName}
                initialLastName={user?.lastName}
              />

              <p className="checkout-methods-intro">
                Al passaggio successivo potrai scegliere come pagare tra:
              </p>

              {/* Metodi disponibili (tutti gestiti da Stripe, nessuna scelta qui) */}
              <div className="checkout-methods">
                {PAYMENT_METHODS.map((method) => (
                  <span key={method} className="checkout-method-pill">
                    {method}
                  </span>
                ))}
              </div>

              {payError && (
                <div className="checkout-error" role="alert">
                  <i className="bi bi-exclamation-triangle-fill me-2" />
                  {payError}
                </div>
              )}

              {/* Note sicurezza */}
              <div className="checkout-security">
                <i className="bi bi-shield-lock-fill me-2" />
                <span>
                  I tuoi dati di pagamento sono gestiti in modo sicuro da{' '}
                  <strong>Stripe</strong>. Non memorizziamo i dati della tua
                  carta.
                </span>
              </div>
            </div>
          </div>

          {/* ── Colonna destra: riepilogo ── */}
          <div className="col-12 col-lg-5">
            <div className="checkout-card checkout-summary">
              <h2 className="checkout-section-title">
                <i className="bi bi-receipt me-2" />
                Riepilogo ordine
              </h2>

              <div className="checkout-summary-items">
                {items.map((item) => (
                  <div key={item.course.id} className="checkout-summary-item">
                    <span className="checkout-summary-item-title">
                      {item.course.title}
                    </span>
                    <span className="checkout-summary-item-price">
                      € {item.course.price.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>

              <div className="checkout-summary-total">
                <span>Totale</span>
                <span>€ {totalPrice.toFixed(2)}</span>
              </div>

              <p className="checkout-summary-vat">
                IVA inclusa ove applicabile
              </p>

              {/* CTA pagamento */}
              <button
                className="checkout-pay-btn"
                onClick={handlePayment}
                disabled={isCreatingSession}
              >
                {isCreatingSession ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                    Elaborazione...
                  </>
                ) : (
                  <>
                    <i className="bi bi-lock-fill me-2" />
                    Paga € {totalPrice.toFixed(2)}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
